import { XBIN_BODY, parentUuidWithoutHyphens, parseXbin } from "./xbin";
import type { XbinDocument } from "./types";

export type MedbinCheck = {
  id: string;
  pass: boolean;
  detail: string;
};

export type MedbinValidation = {
  ok: boolean;
  document: XbinDocument | null;
  parentReference: string | null;
  checks: MedbinCheck[];
};

function check(id: string, pass: boolean, detail: string): MedbinCheck {
  return { id, pass, detail };
}

export function validateXbinXml(xml: string, consumerName: string): MedbinValidation {
  const checks: MedbinCheck[] = [];
  checks.push(check(
    "customer",
    consumerName.trim().length > 0,
    consumerName.trim().length > 0
      ? `Customer: ${consumerName.trim()}`
      : "A customer name is required. Every file belongs to one Communicate customer.",
  ));
  checks.push(check(
    "encoding",
    xml.startsWith('<?xml version="1.0" encoding="iso-8859-1"?>'),
    "XML declaration must be version 1.0, ISO-8859-1.",
  ));

  let document: XbinDocument | null = null;
  try {
    document = parseXbin(xml);
  } catch (err) {
    checks.push(check(
      "parse",
      false,
      err instanceof Error ? err.message : "Could not parse XBIN",
    ));
    return { ok: false, document: null, parentReference: null, checks };
  }

  const expectedVersion = document.kind === "XBIN01" ? "XB0131X" : "XB0210X";
  checks.push(check(
    "codes",
    true,
    `${document.kind} uses version ${expectedVersion}.`,
  ));
  const expectedBody = XBIN_BODY[document.kind];
  checks.push(check(
    "body",
    document.bodyName === expectedBody,
    document.bodyName === expectedBody
      ? `${document.kind} body element is ${expectedBody}.`
      : `${document.kind} body element must be ${expectedBody}, found ${document.bodyName}.`,
  ));
  const hyphenated = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    document.objectIdentifier,
  );
  checks.push(check(
    "uuid",
    hyphenated,
    hyphenated
      ? "Object id is a UUID with hyphens."
      : "Object id must be a UUID with hyphens on the MEDBIN.",
  ));
  const parentReference = hyphenated
    ? parentUuidWithoutHyphens(document.objectIdentifier)
    : null;
  checks.push(check(
    "parent-ref",
    parentReference !== null && !parentReference.includes("-"),
    parentReference
      ? `Parent message must cite ${parentReference} with the hyphens removed.`
      : "No parent reference until the object id is a UUID.",
  ));
  checks.push(check(
    "size",
    document.sizeMatches,
    document.sizeMatches
      ? `Size ${document.originalObjectSize} bytes matches the file.`
      : `Declared size ${document.originalObjectSize} does not match the decoded file.`,
  ));
  checks.push(check(
    "patient",
    document.patientCpr.length > 0,
    document.patientCpr.length > 0 ? "Patient id is present." : "Patient id is missing.",
  ));
  checks.push(check(
    "parties",
    document.sender.ean.length > 0 && document.receiver.ean.length > 0,
    "Sender and receiver EAN are present.",
  ));

  return { ok: checks.every((item) => item.pass), document, parentReference, checks };
}
